import { AxiosClient } from '_api/AxiosClient';
import { config } from '_config';

const { customerApiUrl } = config;

class CustomerApi extends AxiosClient {
  constructor(url) {
    super(url);
    this.resetPassword = this.resetPassword.bind(this);
    this.setPassword = this.setPassword.bind(this);
    this.getMe = this.getMe.bind(this);
    this.updateMe = this.updateMe.bind(this);
    this.getPaymentMethods = this.getPaymentMethods.bind(this);
    this.createPaymentMethod = this.createPaymentMethod.bind(this);
    this.getSubscriptions = this.getSubscriptions.bind(this);
    this.getOrders = this.getOrders.bind(this);
    this.getOrder = this.getOrder.bind(this);
    this.updateOrder = this.updateOrder.bind(this);
    this.updateProduct = this.updateProduct.bind(this);
    this.startMessaging = this.startMessaging.bind(this);
    this.createVoucher = this.createVoucher.bind(this);
    this.cancelAll = this.cancelAll.bind(this);
    this._mergeControllers = this._mergeControllers.bind(this);
    this._controllers = {};
  }

  _mergeControllers(controllerObj = {}) {
    this._controllers = { ...this._controllers, ...controllerObj };
  }

  cancelAll() {
    Object.values(this._controllers)
      .filter((controller) => !controller?.signal?.aborted)
      .forEach((controller) => {
        controller?.abort();
      });
    this._controllers = {};
  }

  resetPassword({ email, category }) {
    const path = `/customers/reset-password`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.post(path, {}, { params: { email, category }, signal: controller.signal });
  }

  setPassword({ token, password, category }) {
    const path = `/customers/set-password`;
    this.Authorization = `Bearer ${token}`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.post(path, { password, category }, { signal: controller.signal });
  }

  getMe() {
    const path = `/customers/me`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.get(path, { signal: controller.signal });
  }

  updateMe({ customerId, body }) {
    const path = `/customers/${customerId}`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.put(path, body, { signal: controller.signal });
  }

  getPaymentMethods({ customerId }) {
    const path = `/customers/${customerId}/payment-methods`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.get(path, { signal: controller.signal });
  }

  createPaymentMethod({ customerId, body }) {
    const path = `/customers/${customerId}/payment-methods`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.post(path, body, { signal: controller.signal });
  }

  getSubscriptions({ customerId }) {
    const path = `/customers/${customerId}/subscriptions`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.get(path, { signal: controller.signal });
  }

  getOrders({ customerId, category, page, count }) {
    const path = `/customers/${customerId}/orders`;
    const params = {};

    if (category) {
      params.category = category;
    }

    if (page) {
      params.page = page;
    }

    if (count) {
      params.count = count;
    }

    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });

    return this.client.get(path, { params, signal: controller.signal });
  }

  getOrder({ customerId, orderId }) {
    const path = `/customers/${customerId}/orders/${orderId}`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.get(path, { signal: controller.signal });
  }

  updateOrder({ customerId, orderId, body }) {
    const path = `/customers/${customerId}/orders/${orderId}`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.put(path, body, { signal: controller.signal });
  }

  updateProduct({ customerId, productId, body }) {
    const path = `/customers/${customerId}/products/${productId}`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.put(path, body, { signal: controller.signal });
  }

  startMessaging({ customerId, productId }) {
    const path = `/customers/${customerId}/product/${productId}/start-messaging`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.get(path, { signal: controller.signal });
  }

  createVoucher({ customerId, productId }) {
    const path = `/customers/${customerId}/product/${productId}/create-voucher`;
    const controller = new AbortController();
    this._mergeControllers({ [path]: controller });
    return this.client.post(path, {}, { signal: controller.signal });
  }
}

const ApiInstance = new CustomerApi(customerApiUrl);

export { ApiInstance as CustomerApi };
